import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Search, Plus, Clock, Calendar, Share2 } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';

const MeetingList = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchMeetings = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        setIsLoading(true);
        const response = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings'
        );
        const data = await response.json();
        
        if (isMounted) {
          setMeetings(data);
          setFilteredMeetings(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching meetings:', error);
          setError('Failed to load meetings. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMeetings();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    const filtered = meetings.filter(meeting =>
      meeting.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMeetings(filtered);
  }, [searchTerm, meetings]);

  if (!isLoaded) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme theme-transition">
      {/* Header */}
      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-theme">
                Your Meetings
              </h1>
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <button
              onClick={() => navigate('/meetings/new')}
              className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto"
            >
              <Plus size={18} />
              New Meeting
            </button>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 pl-10 rounded-lg bg-secondary text-theme border border-theme focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
            <Search className="absolute left-3 top-3.5 text-accent" size={18} />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : filteredMeetings.length === 0 ? (
          <div className="text-center py-12">
            <Video className="mx-auto h-12 w-12 text-accent" />
            <h3 className="mt-2 text-sm font-medium text-theme">No meetings</h3>
            <p className="mt-1 text-sm text-accent">
              Get started by recording a new meeting.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/meetings/new')}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                New Meeting
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {filteredMeetings.map((meeting) => (
              <div
                key={meeting.id}
                onClick={() => navigate(`/meetings/${meeting.id}`)}
                className="p-4 sm:p-6 rounded-lg cursor-pointer bg-theme border border-theme hover:bg-secondary transform transition-all duration-200 hover:scale-[1.02] theme-transition"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-theme">
                      {meeting.title}
                    </h3>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-sm text-accent">
                        <Calendar size={14} />
                        {new Date(meeting.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-accent">
                        <Clock size={14} />
                        {new Date(meeting.date).toLocaleTimeString()}
                      </div>
                      {meeting.shared_by && (
                        <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-primary/10 text-primary">
                          <Share2 size={12} />
                          <span className="truncate max-w-[150px]">Shared by {meeting.shared_by_name || 'Unknown'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-secondary">
                    <Video size={20} className="text-accent" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MeetingList; 